import { Form, Textarea, Input, Button, Select } from "@renderbus/ui";
import { FormInstance } from "antd";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { useValidator } from "../hooks/useValidator";
import styled from "styled-components";
import { AssistantQrcode } from "./assistant-qrcode";
import { QrcodeType, Provinces, Cities } from "../constants";
import WarnIcon from "@/assets/icons/topup/warn";
import request from "project-config/services/request/request";
import { useEffect, useState } from "react";
import { useFetchLotteryActivityInfoList } from "../hooks/useFetchLotteryActivityInfoList";

export function CrashForm({
  onCloseModal,
  form,
  currentActivityID,
}: {
  onCloseModal: () => void;
  form: FormInstance;
  currentActivityID: number;
}) {
  const t = getTranslateNsT("topup");
  const { nameValidator, phoneValidator, addressValidator } = useValidator();
  const { lotteryActivityInfoList } = useFetchLotteryActivityInfoList();

  const getCityData = (proinceValue: string) => {
    return Cities[proinceValue].map((item: { name: string }) => {
      return {
        value: item.name,
        label: item.name,
      };
    });
  };
  const initProinceData = Provinces.map((item) => {
    return {
      value: item.code,
      label: item.name,
    };
  });
  const [currentProvince, setCurrentProvince] = useState(
    initProinceData[0].value
  );
  const [currentCity, setCurrentCity] = useState(
    getCityData(initProinceData[0].value)[0].value
  );
  const handleSubmit = () => {
    form
      .validateFields()
      .then(async () => {
        const param = {
          receivingInfo: JSON.stringify({
            name: form.getFieldValue("name"),
            phone: form.getFieldValue("phone"),
            address: `${
              Provinces.find((item) => item.code === currentProvince)?.name
            }${currentCity}${form.getFieldValue("address")}`,
          }),
          activityId: currentActivityID,
        };
        const res = await request.post(
          "/api/rendering/user/activity/template/saveReceivingInfo",
          param
        );
        if (res.code !== 200) return;
        onCloseModal();
        form.resetFields();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (
      !lotteryActivityInfoList.length ||
      !lotteryActivityInfoList[0].receivingInfo
    )
      return;
    const { name, phone, address } = JSON.parse(
      lotteryActivityInfoList[0].receivingInfo
    );
    const result =
      Provinces.filter((item) => address.includes(item.name)) ?? [];
    const province = result.reduce((pre, cur) => {
      return address.indexOf(pre.name) > address.indexOf(cur.name) ? cur : pre;
    });
    const city =
      getCityData(province.code).find((item) => address.includes(item.value))
        ?.value ?? "";
    setCurrentProvince(province.code);
    setCurrentCity(city);
    form.setFieldsValue({
      name,
      phone,
      address: address.split(`${province.name}${city}`)[1],
    });
  }, [lotteryActivityInfoList, form]);

  return (
    <CrashFormWrapper>
      <Form
        colon={false}
        requiredMark={false}
        form={form}
        labelCol={{ span: 6 }}
        labelAlign="right"
      >
        <Form.Item
          name="name"
          label={t("topup_lottery_recipient")}
          rules={[{ validator: (...arg) => nameValidator(arg) }]}
        >
          <Input
            placeholder={t("topup_lottery_recipient_placeholder")}
            className="background-2C2C31"
          />
        </Form.Item>
        <Form.Item
          name="phone"
          label={t("topup_lottery_information")}
          rules={[{ validator: (...arg) => phoneValidator(arg) }]}
        >
          <Input
            placeholder={t("topup_lottery_information_placeholder")}
            className="background-2C2C31"
          />
        </Form.Item>
        <Form.Item label={t("topup_lottery_address")}>
          <>
            <CashAddressContainer>
              <Select
                showSearch
                options={initProinceData}
                filterOption={(input, option) =>
                  (option?.label ?? "").toString().includes(input)
                }
                className="address-select"
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                allowClear={false}
                value={currentProvince}
                onChange={(value: string) => {
                  setCurrentCity(getCityData(value)[0].value);
                  setCurrentProvince(value);
                }}
              />
              <Select
                options={currentProvince ? getCityData(currentProvince) : []}
                className="address-select"
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                value={currentCity}
                allowClear={false}
                onChange={(value: string) => {
                  setCurrentCity(value);
                }}
              />
            </CashAddressContainer>
            <Form.Item
              name="address"
              rules={[{ validator: (...arg) => addressValidator(arg) }]}
            >
              <Textarea
                placeholder={t("topup_lottery_address_placeholder")}
                className="background-2C2C31"
                rows={4}
              />
            </Form.Item>
          </>
        </Form.Item>
      </Form>
      <AssistantQrcode type={QrcodeType.SumbitCashInformation} />
      <Button onClick={handleSubmit}>{t("topup_lottery_confirm")}</Button>
      <div className={"warn-tip"}>
        <WarnIcon />
        {t("topup_lottery_edit_tip")}
      </div>
    </CrashFormWrapper>
  );
}

const CrashFormWrapper = styled.div`
  width: 426px;
  margin: 10px auto 40px 150px;
  button {
    margin-top: 118px;
    width: 136px;
    padding: 10px;
    height: 40px;
    line-height: 1;
    background: var(--main-color) !important;
    margin-left: 190px;
  }
  .warn-tip {
    font-size: 12px;
    line-height: 17px;
    color: #727e84;
    margin-top: 12px;
    text-align: center;
    margin-left: 100px;
  }
  svg {
    margin-right: 5px;
  }
`;

const CashAddressContainer = styled.div`
  display: flex;
  margin-bottom: 12px;
  .address-select {
    background: #2c2c31;
    min-width: 150px;
    margin-right: 20px;
    .ant-select-selector {
      background: #2c2c31 !important;
      border-radius: 3px;
      font-size: 14px;
      box-shadow: none !important;
    }
  }
`;
